import { Link, useParams } from "react-router-dom";
import { ChevronRight, Clock } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import {
  InShort, DiligenceBox, RiskBox, Note, PeopleGrid, ComparisonTable,
  QvTable, PackagesTable, Checklist, SourcesList, GlossaryList, RelatedLinks, Exhibit, VerdictBox,
} from "../components/Blocks";
import { getArticle, getFaq } from "../content";
import { SITE_DOMAIN, LAST_REVIEWED, LAST_REVIEWED_LABEL } from "../content/site";
import NotFoundPage from "./NotFoundPage";

const renderInline = (text) => {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!m) return part;
    const [, label, href] = m;
    if (href.startsWith("/")) return <Link key={i} to={href}>{label}</Link>;
    return <a key={i} href={href} target="_blank" rel="noopener noreferrer">{label}</a>;
  });
};

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Block = ({ block }) => {
  switch (block.t) {
    case "h2":
      return <h2 id={slugify(block.text)} className="font-serif-display font-light text-3xl md:text-4xl mt-14 mb-5 scroll-mt-32" style={{ color: "var(--ink)" }}>{block.text}</h2>;
    case "h3":
      return <h3 className="font-serif-display text-2xl mt-10 mb-4" style={{ color: "var(--ink)" }}>{block.text}</h3>;
    case "p":
      return <p>{renderInline(block.text)}</p>;
    case "list":
      return (
        <ul className="my-6 pl-0 list-none space-y-3 max-w-[68ch]">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 leading-relaxed">
              <span style={{ color: "var(--accent)" }}>·</span>
              <span>{renderInline(item)}</span>
            </li>
          ))}
        </ul>
      );
    case "table":
      return (
        <div className="overflow-x-auto my-8">
          <table className="w-full text-left border-collapse" style={{ minWidth: 520 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--ink)" }}>
                {block.head.map((h) => (
                  <th key={h} className="py-3 pr-4 text-xs uppercase tracking-[0.15em] font-medium" style={{ color: "var(--ink-muted)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} style={{ borderBottom: "1px solid var(--line)" }}>
                  {row.map((cell, j) => (
                    <td key={j} className="py-3.5 pr-4 text-sm align-top" style={{ color: j === 0 ? "var(--ink)" : "var(--ink-muted)", fontWeight: j === 0 ? 500 : 400 }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "diligence": return <DiligenceBox title={block.title} items={block.items} />;
    case "risk": return <RiskBox />;
    case "note": return <Note>{block.text}</Note>;
    case "people": return <PeopleGrid />;
    case "comparison": return <ComparisonTable />;
    case "qvtable": return <QvTable />;
    case "packages": return <PackagesTable />;
    case "checklist": return <Checklist />;
    case "sources": return <SourcesList />;
    case "glossary": return <GlossaryList items={block.items} />;
    case "exhibit": return <Exhibit src={block.src} alt={block.alt} caption={block.caption} label={block.label} tid={block.tid} />;
    case "verdict": return <VerdictBox verdict={block.verdict} verified={block.verified} open={block.open} />;
    default: return null;
  }
};

export default function ArticlePage({ kind = "article" }) {
  const { slug } = useParams();
  const data = kind === "faq" ? getFaq(slug) : getArticle(slug);
  if (!data) return <NotFoundPage />;

  const path = kind === "faq" ? `/faq/${slug}` : `/${slug}`;
  const words = (data.blocks || [])
    .map((b) => [b.text, ...(b.items || []).map((i) => (typeof i === "string" ? i : i.def || ""))].filter(Boolean).join(" "))
    .join(" ")
    .split(/\s+/).length;
  const readingTime = Math.max(1, Math.round(words / 200));
  const toc = (data.blocks || []).filter((b) => b.t === "h2").map((b) => ({ id: slugify(b.text), text: b.text }));

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: data.h1,
        description: data.meta,
        dateModified: LAST_REVIEWED,
        author: { "@type": "Organization", name: "U.CENTER Explained", url: SITE_DOMAIN },
        mainEntityOfPage: `${SITE_DOMAIN}${path}`,
        isPartOf: { "@type": "WebSite", name: "U.CENTER Explained", url: SITE_DOMAIN },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_DOMAIN },
          { "@type": "ListItem", position: 2, name: data.section, item: `${SITE_DOMAIN}${kind === "faq" ? "/faq/is-ucenter-a-bank" : path}` },
          { "@type": "ListItem", position: 3, name: data.h1, item: `${SITE_DOMAIN}${path}` },
        ],
      },
      ...(data.faq
        ? [{
            "@type": "FAQPage",
            mainEntity: data.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }]
        : []),
    ],
  };

  const scrollTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -110 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <article data-testid={`article-${slug}`} className="max-w-6xl mx-auto px-5 pt-12 md:pt-16">
      <Seo title={data.title} description={data.meta} path={path} jsonLd={jsonLd} />

      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs mb-10 flex-wrap" style={{ color: "var(--ink-muted)" }} data-testid="breadcrumbs">
        <Link to="/" className="no-underline hover:underline" style={{ color: "var(--ink-muted)" }}>Home</Link>
        <ChevronRight size={12} />
        <span>{data.section}</span>
        <ChevronRight size={12} />
        <span style={{ color: "var(--ink)" }}>{data.h1}</span>
      </nav>

      <div className="grid gap-12 lg:grid-cols-[1fr_240px]">
        <div className="max-w-[720px]">
          <Reveal y={20}>
            <p className="text-xs uppercase tracking-[0.25em] font-medium mb-5" style={{ color: "var(--accent)" }}>{data.section}</p>
            <h1 className="font-serif-display font-light text-4xl md:text-6xl leading-[1.08] m-0" style={{ color: "var(--ink)", letterSpacing: "-0.01em" }}>{data.h1}</h1>
            <div className="flex items-center gap-5 mt-6 text-xs" style={{ color: "var(--ink-muted)" }}>
              <span className="flex items-center gap-1.5"><Clock size={13} /> {readingTime} min read</span>
              <span>Last reviewed {LAST_REVIEWED_LABEL}</span>
            </div>
            <InShort>{data.inShort}</InShort>
          </Reveal>

          <div className="prose-article">
            {(data.blocks || []).map((block, i) => (
              <Reveal key={i} y={16}>
                <Block block={block} />
              </Reveal>
            ))}
          </div>

          {data.related && <RelatedLinks links={data.related} />}
        </div>

        {toc.length > 1 && (
          <aside className="hidden lg:block" data-testid="toc">
            <div className="sticky top-32">
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-4" style={{ color: "var(--ink-muted)" }}>On this page</p>
              <ul className="space-y-2.5 p-0 m-0 list-none border-l" style={{ borderColor: "var(--line)" }}>
                {toc.map((item) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`} onClick={(e) => scrollTo(e, item.id)} className="block pl-4 text-sm no-underline transition-colors hover:underline" style={{ color: "var(--ink-muted)" }}>
                      {item.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}
      </div>
    </article>
  );
}
